import React, { useCallback, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Header, LeftHeader, Icons, DropDiv, SearchHeader, Selects, Text } from './styles';
import {
  CHAT_GROUP_TYPE,
  PROJECT_ICON_TYPE,
  ICON_COLORS,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  USER_ROLE
} from '../../../global/constants';
import MessageHeaderLoading from '../../loading/messageHeaderLoading';
import ChatBoxAvatar from '../../avatar/chatboxAvatar';
import ResponsiveHeader from '../../responsiveHeader';
import ProjectIconAvatar from '../../projectIconAvatar';
import ChatGroupDropdown from './chatGroupDropdown';
import MemberDropdown from './memberDropdown';
import IconButton from '../../button/iconButton';
import BottomNavigationItem from '../../bottomNavigation/subNavigation/bottomNavigationItem';
import CustomSelect from '../../customSelect';
import { rootStore } from '../../../mobx/rootStore';
import { ROUTES } from '../../../routes/urls';
import { useHistory } from 'react-router-dom';
import { useMobileDevice } from '../../../global/useMobile';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { isEmpty } from '../../../helpers/common';
import { ChatGroupInterface } from '../../../interfaces/chatMessageInterface';
import { createMessageGroup } from '../../../services/chatMessageService';
import { ModalName } from './hooks/useGroupModals';
import { useGroupActions } from './hooks/useGroupActions';

interface Props {
  selectFile: boolean;
  handleMenuItemClick: (key: number | string) => void;
  newGroup?: boolean;
  onCloseNewGroup: () => void;
  loadData: () => void;
  loading: boolean;
  open: (modal: ModalName, userId?: string) => void;
  actions: ReturnType<typeof useGroupActions>['actions'];
}

const MessageDetailHeader = ({
  selectFile,
  handleMenuItemClick,
  newGroup,
  onCloseNewGroup,
  loadData,
  loading,
  open,
  actions
}: Props) => {
  // Refs Variable
  const memberIconRef = useRef<HTMLDivElement | null>(null);
  // mobx store variable
  const { workspaceStore, currentUser, projectStore, chatMessageStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const { projects } = projectStore;
  const { currentChatGroup, chatGroups } = chatMessageStore;
  const { messages } = currentChatGroup || {};
  // state variables
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  // other variables
  const history = useHistory();
  const mobile = useMobileDevice();

  // handles expand chat
  const onExpandChat = useCallback(() => {
    if (!currentChatGroup?.id) return;
    rootStore.projectStore.setCurrentProject(currentChatGroup?.id);
    history.push(ROUTES.PROJECT_MESSAGES.replace(':id', currentChatGroup?.id || ''));
  }, [currentChatGroup?.id, history]);

  // renders more icon
  const renderMoreIcon = useCallback(() => {
    return mobile ? (
      <BottomNavigationItem iconName='three-dots-horizontal-icon' />
    ) : (
      <IconButton
        iconName='three-dots-horizontal-icon'
        size={ICON_BUTTON_SIZE.MEDIUM}
        variant={ICON_BUTTON_TYPE.TERTIARY}
      />
    );
  }, [mobile]);

  // renders project icon
  const renderProjectIcon = (groupId: string) => {
    const projectDetails = projects?.find((item) => item?.id === groupId);
    return (
      <ProjectIconAvatar
        size={30}
        type={projectDetails?.icon?.type ?? PROJECT_ICON_TYPE.DEFAULT}
        reference={projectDetails?.icon?.reference || ''}
        backgroundColor={ICON_COLORS[projectDetails?.icon?.color || 1]}
        projectName={projectDetails?.name || ''}
        borderRadius={'50px'}
        iconSize={16}
      />
    );
  };

  // renders expand chat icon
  const renderExpandChatIcon = useCallback(() => {
    return (
      <IconButton
        iconName='expand-chat-icon'
        size={mobile ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.MEDIUM}
        variant={ICON_BUTTON_TYPE.TERTIARY}
        onClick={onExpandChat}
      />
    );
  }, [mobile, onExpandChat]);

  // handles click on existing group
  const onClickExistingGroup = useCallback(
    (groupId: string) => {
      const currentGroup = chatGroups?.find((x) => x?.id === groupId);
      if (currentGroup) {
        chatMessageStore.setCurrentChatGroup(currentGroup);
        setSelectedUsers([]);
        onCloseNewGroup();
        if (mobile) history.push(`${ROUTES.NAVBARMESSAGE_DETAILS_MOBILE}?chatGroup=${currentGroup?.id}`);
        else history.push(`${ROUTES.NAVBARMESSAGE}?chatGroup=${currentGroup?.id}`);
        UserPreferenceSingleton.getInstance().setLastMessageGroup(currentGroup?.id);
      }
    },
    [chatGroups, chatMessageStore, history, mobile, onCloseNewGroup]
  );

  // handles change in selected users
  const handleSelectChangeUsers = useCallback(
    (selectedUserIds: string[]) => {
      const lastSelectedId = selectedUserIds[selectedUserIds?.length - 1];
      const isGroup = chatGroups?.some((group) => group.id === lastSelectedId);
      if (isGroup) {
        onClickExistingGroup(lastSelectedId);
      } else {
        setSelectedUsers(selectedUserIds);
      }
    },
    [onClickExistingGroup, chatGroups]
  );

  // checks existing group
  const checkExistingGroup = useCallback(() => {
    return chatGroups?.find((group) => {
      if (group?.users?.length === selectedUsers?.length + 1 && group?.type === CHAT_GROUP_TYPE.PERSONAL) {
        const users = group?.users?.map((x) => x?.id);
        const notExist = selectedUsers.some((id) => !users.includes(id));
        return !notExist;
      }
      return false;
    });
  }, [chatGroups, selectedUsers]);

  // handles existing group
  const handleExistingGroup = useCallback(
    (existingGroup: ChatGroupInterface) => {
      setSelectedUsers([]);
      onCloseNewGroup();
      chatMessageStore.setCurrentChatGroup(existingGroup);
      UserPreferenceSingleton.getInstance().setLastMessageGroup(existingGroup?.id);
      const path = mobile
        ? `/messages/details?chatGroup=${existingGroup?.id}`
        : `/messages?chatGroup=${existingGroup?.id}`;
      history.push(path);
    },
    [chatMessageStore, history, mobile, onCloseNewGroup]
  );

  // creates new group
  const createNewGroup = useCallback(async () => {
    if (!currentUser?.id || !currentWorkspace?.id || !selectedUsers?.length) return;
    const payload = {
      userId: currentUser?.id,
      name: '',
      companyId: currentWorkspace?.id,
      users: selectedUsers
    };
    const result = await createMessageGroup(payload);
    if (result) {
      setSelectedUsers([]);
      onCloseNewGroup();
      chatMessageStore.setCurrentChatGroup(result);
      UserPreferenceSingleton.getInstance().setLastMessageGroup(result?.id);
      const path = mobile ? `/messages/details?chatGroup=${result?.id}` : `/messages?chatGroup=${result?.id}`;
      history.push(path);
      loadData();
    }
  }, [
    currentUser?.id,
    currentWorkspace?.id,
    selectedUsers,
    onCloseNewGroup,
    chatMessageStore,
    mobile,
    history,
    loadData
  ]);

  // handles create group
  const onCreateGroup = useCallback(async () => {
    if (!selectedUsers?.length) return;
    const existingGroup = checkExistingGroup();
    if (existingGroup && !isEmpty(existingGroup?.id)) {
      handleExistingGroup(existingGroup);
    } else {
      await createNewGroup();
    }
  }, [selectedUsers?.length, checkExistingGroup, handleExistingGroup, createNewGroup]);

  // new group header
  if (newGroup) {
    return (
      <>
        {mobile && <ResponsiveHeader title='New Chat' noMargin={true} />}
        <SearchHeader className='searchbar'>
          <Selects tagsSelected={(selectedUsers?.length || 0) > 0}>
            <Text>To:</Text>
            <CustomSelect
              users={
                currentWorkspace?.users?.filter((user) => {
                  // Filter out agent users
                  if (user?.id?.startsWith('agent_')) return false;

                  // If current user is a client, only show admin/owner users
                  if (currentUser?.role === USER_ROLE.CLIENT) {
                    return user.isAdmin || user.isOwner;
                  }

                  // For non-client users, show all users except clients (unless admin/owner)
                  if (user.role !== USER_ROLE.CLIENT) return true;

                  // Only include client users if current user is admin/owner
                  return currentUser?.isAdmin || currentUser?.isOwner;
                }) || []
              }
              groups={
                chatGroups?.map((group) => ({
                  ...group,
                  users: group.users?.map((user) => ({
                    ...user,
                    phone: user.phone || ''
                  }))
                })) || []
              }
              currentUserId={currentUser?.id || ''}
              value={selectedUsers}
              onChange={handleSelectChangeUsers}
              onBlur={onCreateGroup}
              placeholder='Enter name or Project name'
              autoFocus={newGroup}
            />
          </Selects>
        </SearchHeader>
      </>
    );
  }

  // Message loading header
  if (loading && (!messages || messages?.length === 0)) {
    return <MessageHeaderLoading />;
  }

  // File header
  if (selectFile) {
    return <></>;
  }

  // Message header
  if (mobile) {
    return (
      <ResponsiveHeader
        title={currentChatGroup?.name || ''}
        applyDottedText={true}
        noMargin={true}
        leftElement={
          currentChatGroup?.type === CHAT_GROUP_TYPE.PROJECT ? (
            renderExpandChatIcon()
          ) : (
            <MemberDropdown
              currentChatGroup={currentChatGroup}
              currentUser={currentUser}
              currentWorkspace={currentWorkspace}
              mobile={mobile}
              memberIconRef={memberIconRef}
              onOpenAddMemberModal={() => open('member')}
              onRemoveMember={(userId: string) => open('alert', userId)}
            />
          )
        }
      />
    );
  }

  return (
    <Header className={currentChatGroup?.type === CHAT_GROUP_TYPE.PROJECT ? 'project' : ''}>
      <LeftHeader>
        {currentChatGroup?.type === CHAT_GROUP_TYPE.PROJECT && renderProjectIcon(currentChatGroup?.id)}
        {currentChatGroup?.users && currentChatGroup?.type !== CHAT_GROUP_TYPE.PROJECT && (
          <ChatBoxAvatar
            imgSrc={currentChatGroup?.profilePic || ''}
            name={currentChatGroup?.name}
            size={30}
            users={currentChatGroup?.users}
          />
        )}
        <p>{currentChatGroup?.name}</p>
      </LeftHeader>
      <Icons className={currentChatGroup?.type !== CHAT_GROUP_TYPE.PROJECT ? 'normal-group' : ''}>
        {currentChatGroup?.type === CHAT_GROUP_TYPE.PROJECT && (
          <>
            {renderExpandChatIcon()}
            <ChatGroupDropdown
              chatGroupType={currentChatGroup?.type}
              currentChatGroup={currentChatGroup}
              currentUser={currentUser}
              currentWorkspace={currentWorkspace}
              onPinChatGroup={actions.handlePinChatGroup}
              onOpenEditGroupModal={() => {
                if (currentChatGroup) chatMessageStore.setCurrentChatGroupName(currentChatGroup);
                open('editGroup');
              }}
              onOpenClearMessageConfirmation={() => open('clearMessage')}
              onRemoveMember={(userId: string) => open('alert', userId)}
              onMenuItemClick={handleMenuItemClick}
              renderMoreIcon={renderMoreIcon}
            />
          </>
        )}
        {currentChatGroup?.type !== CHAT_GROUP_TYPE.PROJECT && (
          <React.Fragment>
            <MemberDropdown
              currentChatGroup={currentChatGroup}
              currentUser={currentUser}
              currentWorkspace={currentWorkspace}
              mobile={mobile}
              memberIconRef={memberIconRef}
              onOpenAddMemberModal={() => open('member')}
              onRemoveMember={(userId: string) => open('alert', userId)}
            />
            <DropDiv>
              <ChatGroupDropdown
                chatGroupType={currentChatGroup?.type}
                currentChatGroup={currentChatGroup}
                currentUser={currentUser}
                currentWorkspace={currentWorkspace}
                onPinChatGroup={actions.handlePinChatGroup}
                onOpenEditGroupModal={() => {
                  if (currentChatGroup) chatMessageStore.setCurrentChatGroupName(currentChatGroup);
                  open('editGroup');
                }}
                onOpenClearMessageConfirmation={() => open('clearMessage')}
                onRemoveMember={(userId: string) => open('alert', userId)}
                onMenuItemClick={handleMenuItemClick}
                renderMoreIcon={renderMoreIcon}
              />
            </DropDiv>
          </React.Fragment>
        )}
      </Icons>
    </Header>
  );
};

export default observer(MessageDetailHeader);
